import { useState } from "react"
import { config } from "@/config"
import useAxios from "@/hooks/useAxios"
import { encryptText, lastFourOf, normalizeDigits } from "@/utils/symmetricCrypto"

type PaymentInstrumentFormData = {
    type: string;
    accountName: string;
    accountNumber: string;
    routingNumber: string;
    isDefault?: boolean;
};

export default function usePaymentInstrumentForm(onSuccess: Function) {
    const [processing, setProcessing] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const axios = useAxios()

    async function submitPaymentInstrument(form: PaymentInstrumentFormData) {
        try {
            setProcessing(true);
            const accountNumber = normalizeDigits(form.accountNumber);
            const routingNumber = normalizeDigits(form.routingNumber);
            const accountName = form.accountName.trim();

            if (!accountName || !accountNumber || !routingNumber) {
                setErrorMessage("accountName, accountNumber and routingNumber are required");
                return;
            }

            const [encryptedAccountName, encryptedAccountNumber, encryptedRoutingNumber] = await Promise.all([
                encryptText(accountName, config.paymentCryptoKey),
                encryptText(accountNumber, config.paymentCryptoKey),
                encryptText(routingNumber, config.paymentCryptoKey)
            ]);

            await axios.post(`${config.backendUrl}${config.paymentInstrumentsUrl}`, {
                type: form.type,
                accountName: encryptedAccountName,
                accountNumber: encryptedAccountNumber,
                routingNumber: encryptedRoutingNumber,
                lastFour: lastFourOf(accountNumber),
                isDefault: !!form.isDefault
            })
            onSuccess()
        } catch (err) {
            console.error(err)
            setErrorMessage('Failed to create payment instrument: ' + err)
        } finally {
            setProcessing(false)
        }
    }

    function onClear() {
        setErrorMessage('')
    }

    return {
        processing, errorMessage, setErrorMessage, onClear, submitPaymentInstrument
    }
}
